import { Component, Inject, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { BulkOperationNotificationType } from '../../core/enums/bulk-operation-notification-type.enum';
import { finalize } from 'rxjs/operators';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { TicketDetailsComponent } from '../ticket-details.component';
import { ReplyBulkOperationsNotificationService } from '../../core/services/reply-bulk-operations-notification.service';
import { ReplyBulkOperationsService } from '../../core/services/reply-bulk-operations.service';
import { ConfirmationModalComponent } from '../../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { BulkUpdateRepliesVisibilityBody } from '../../core/models/bulk-update-replies-visibility-body.model';
import { BulkOperationResultWithData } from '../../core/models/bulk-operation-result-with-data.model';

@Component({
    selector: 'app-replies-bulk-operation-menu',
    templateUrl: './replies-bulk-operation-menu.component.html',
    styleUrl: './replies-bulk-operation-menu.component.scss',
    imports: [TranslateModule],
})
export class RepliesBulkOperationMenuComponent {
    @Input() ticket: DetailedTicketDTO;

    isSending = false;

    private unsubscriber = new Subject<void>();

    constructor(
        @Inject(TicketDetailsComponent) public parent: TicketDetailsComponent,
        private bsModalService: BsModalService,
        private replyBulkOperationsService: ReplyBulkOperationsService,
        private replyBulkOperationsNotificationService: ReplyBulkOperationsNotificationService,
        private translateService: TranslateService,
    ) {}

    cancel(replies: ThreadElementDTO[] | any[]): void {
        replies.forEach((reply: ThreadElementDTO) => {
            reply.isSelected = false;
            if (reply.subReplies) {
                this.cancel(reply.subReplies);
            }
        });
        this.ticket.selectedRepliesCount.set(0);
    }

    changeRepliesVisibility(isVisible: boolean): void {
        this.isSending = true;
        const updateData: BulkUpdateRepliesVisibilityBody = {
            isVisible,
            replyIds: this.getSelectedReplies(this.ticket.messageThread as ThreadElementDTO[]),
        };

        this.replyBulkOperationsService
            .changeRepliesVisibility(this.ticket.id, updateData)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSending = false;
                }),
            )
            .subscribe((data: BulkOperationResultWithData) => {
                this.parent.loadedThread = this.parent.loaded = false;
                this.parent.isTicketReady = false;
                this.parent.processLoadTicket(data.ticket);
                this.replyBulkOperationsNotificationService.showNotificationForReplies(
                    data,
                    isVisible ? BulkOperationNotificationType.show : BulkOperationNotificationType.hide,
                );
            });
    }

    openDeleteModal() {
        const initialState = {
            title: this.translateService.instant('Are you sure you want to delete these replies?'),
            confirmText: this.translateService.instant('Delete'),
            declineText: this.translateService.instant('Cancel'),
            confirmCallback: () => {
                return this.deleteReplies();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    private getSelectedReplies(replies: ThreadElementDTO[], ids: number[] = []): number[] {
        replies.forEach((reply: ThreadElementDTO) => {
            if (reply.isSelected) {
                ids.push(reply.id);
            }
            if (reply.subReplies) {
                ids = ids.concat(this.getSelectedReplies(reply.subReplies));
            }
        });
        return ids;
    }

    private deleteReplies(): void {
        this.isSending = true;
        this.replyBulkOperationsService
            .deleteReplies(this.ticket.id, this.getSelectedReplies(this.ticket.messageThread as ThreadElementDTO[]))
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSending = false;
                }),
            )
            .subscribe((data: BulkOperationResultWithData) => {
                this.parent.loadedThread = this.parent.loaded = false;
                this.parent.isTicketReady = false;
                this.parent.processLoadTicket(data.ticket);
                this.replyBulkOperationsNotificationService.showNotificationForReplies(
                    data,
                    BulkOperationNotificationType.delete,
                );
            });
    }
}
